import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Navbar } from '../components/Navbar'
import { useAuth } from '../context/AuthContext'
import { kycAPI } from '../lib/api'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { toast } from 'sonner'
import { Shield, User, FileCheck, Building2, CheckCircle2, Clock, AlertCircle, Loader2, ArrowRight, ArrowLeft } from 'lucide-react'

const countries = [
  'United States', 'United Kingdom', 'Canada', 'Germany', 'France', 
  'Spain', 'Italy', 'Netherlands', 'Switzerland', 'Singapore', 'UAE'
]

export function KYCPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user, updateUser, isAuthenticated } = useAuth()
  const [currentStep, setCurrentStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [existingKYC, setExistingKYC] = useState(null)
  const [checkingStatus, setCheckingStatus] = useState(true)

  const steps = [
    { id: 1, title: t('kyc.personalInfo'), icon: User },
    { id: 2, title: t('kyc.identity'), icon: FileCheck },
    { id: 3, title: t('kyc.accreditation'), icon: Building2 },
    { id: 4, title: t('kyc.review'), icon: Shield },
  ]

  const documentTypes = [
    { value: 'passport', label: t('kyc.passport') },
    { value: 'id_card', label: t('kyc.idCard') },
    { value: 'driver_license', label: t('kyc.driverLicense') },
  ]

  const accreditationTypes = [
    { value: 'income', label: t('kyc.income') },
    { value: 'net_worth', label: t('kyc.netWorth') },
    { value: 'professional', label: t('kyc.professional') },
  ]

  const [formData, setFormData] = useState({
    full_name: '', date_of_birth: '', nationality: '', address: '', city: '', country: '',
    postal_code: '', document_type: '', document_number: '', accreditation_type: '', accreditation_details: '',
  })

  useEffect(() => {
    if (!isAuthenticated) { navigate('/'); return }
    const checkKYCStatus = async () => {
      try { const res = await kycAPI.getStatus(); setExistingKYC(res.data) } catch {}
      finally { setCheckingStatus(false) }
    }
    checkKYCStatus()
  }, [isAuthenticated, navigate])

  const handleInputChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }))
  const nextStep = () => { if (currentStep < 4) setCurrentStep(currentStep + 1) }
  const prevStep = () => { if (currentStep > 1) setCurrentStep(currentStep - 1) }

  const submitKYC = async () => {
    setLoading(true)
    try {
      await kycAPI.submit(formData)
      toast.success('KYC submitted successfully!')
      setTimeout(() => updateUser({ ...user, kyc_status: 'in_review' }), 1000)
      navigate('/investor')
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit KYC')
    } finally { setLoading(false) }
  }

  if (checkingStatus) {
    return (<div className="min-h-screen bg-[#F8FAFC]"><Navbar /><div className="flex items-center justify-center pt-32"><Loader2 className="w-8 h-8 animate-spin text-brand-primary" /></div></div>)
  }

  if (existingKYC) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Navbar />
        <main className="pt-28 pb-16">
          <div className="max-w-lg mx-auto px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-slate-100 p-8 text-center">
              {existingKYC.status === 'approved' ? (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 bg-emerald-100 flex items-center justify-center"><CheckCircle2 className="w-8 h-8 text-emerald-600" /></div>
                  <h2 className="text-2xl font-heading text-brand-secondary mb-2">{t('kyc.approved')}</h2>
                  <div className="w-12 h-px bg-brand-primary mx-auto my-4" />
                  <p className="text-slate-500 mb-6">{t('kyc.approvedSubtitle')}</p>
                  <Button onClick={() => navigate('/marketplace')} className="h-14 px-8 bg-brand-primary hover:bg-brand-primary-hover text-brand-secondary font-semibold rounded-none">
                    {t('kyc.exploreProperties')} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </>
              ) : existingKYC.status === 'in_review' ? (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 bg-amber-100 flex items-center justify-center"><Clock className="w-8 h-8 text-amber-600" /></div>
                  <h2 className="text-2xl font-heading text-brand-secondary mb-2">{t('kyc.underReview')}</h2>
                  <div className="w-12 h-px bg-brand-primary mx-auto my-4" />
                  <p className="text-slate-500 mb-6">{t('kyc.underReviewSubtitle')}</p>
                  <div className="text-left p-4 bg-[#F8FAFC] border border-slate-100 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">{t('kyc.submitted')}</span>
                      <span className="text-brand-secondary font-semibold">{new Date(existingKYC.submitted_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">{t('kyc.status')}</span>
                      <span className="text-amber-600 font-semibold">{t('kyc.inReview')}</span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 bg-red-100 flex items-center justify-center"><AlertCircle className="w-8 h-8 text-red-600" /></div>
                  <h2 className="text-2xl font-heading text-brand-secondary mb-2">{t('kyc.rejected')}</h2>
                  <div className="w-12 h-px bg-brand-primary mx-auto my-4" />
                  <p className="text-slate-500 mb-4">{t('kyc.rejectedSubtitle')}</p>
                  {existingKYC.reviewer_notes && (
                    <div className="text-left p-4 bg-red-50 border border-red-100 mb-6">
                      <p className="text-xs text-red-500 uppercase tracking-wider mb-1">{t('kyc.reason')}</p>
                      <p className="text-sm text-red-600">{existingKYC.reviewer_notes}</p>
                    </div>
                  )}
                  <p className="text-sm text-slate-400">{t('kyc.contactSupport')}</p>
                </>
              )}
            </motion.div>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <main className="pt-28 pb-16">
        <div className="max-w-2xl mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
            <span className="inline-block px-4 py-2 border border-brand-primary/50 bg-brand-primary/10 text-brand-primary text-xs font-semibold uppercase tracking-widest mb-4">
              {t('kyc.badge')}
            </span>
            <h1 className="text-3xl font-heading text-brand-secondary">{t('kyc.title')}</h1>
            <div className="w-16 h-px bg-brand-primary mx-auto my-4" />
            <p className="text-slate-500">{t('kyc.subtitle')}</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-8">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center">
                    <div className={`w-12 h-12 flex items-center justify-center transition-colors ${currentStep >= step.id ? 'bg-brand-primary text-brand-secondary' : 'bg-slate-100 text-slate-400'}`}>
                      <step.icon className="w-5 h-5" />
                    </div>
                    <span className={`text-xs mt-2 uppercase tracking-wider ${currentStep >= step.id ? 'text-brand-secondary font-semibold' : 'text-slate-400'}`}>{step.title}</span>
                  </div>
                  {index < steps.length - 1 && <div className={`flex-1 h-px mx-3 ${currentStep > step.id ? 'bg-brand-primary' : 'bg-slate-200'}`} />}
                </React.Fragment>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white border border-slate-100 p-6">
            {currentStep === 1 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-600 font-semibold">{t('kyc.fullName')}</Label>
                  <Input value={formData.full_name} onChange={(e) => handleInputChange('full_name', e.target.value)}
                    placeholder="John Michael Doe" className="h-12 bg-white border-slate-200 focus:border-brand-primary text-brand-secondary rounded-none" data-testid="kyc-full-name" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600 font-semibold">{t('kyc.dateOfBirth')}</Label>
                  <Input type="date" value={formData.date_of_birth} onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                    className="h-12 bg-white border-slate-200 focus:border-brand-primary text-brand-secondary rounded-none" data-testid="kyc-dob" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600 font-semibold">{t('kyc.nationality')}</Label>
                  <Select value={formData.nationality} onValueChange={(v) => handleInputChange('nationality', v)}>
                    <SelectTrigger className="h-12 bg-white border-slate-200 text-brand-secondary rounded-none" data-testid="kyc-nationality">
                      <SelectValue placeholder={t('kyc.selectNationality')} />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      {countries.map((c) => (<SelectItem key={c} value={c} className="text-brand-secondary hover:bg-brand-primary/10">{c}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-600 font-semibold">{t('kyc.documentType')}</Label>
                  <Select value={formData.document_type} onValueChange={(v) => handleInputChange('document_type', v)}>
                    <SelectTrigger className="h-12 bg-white border-slate-200 text-brand-secondary rounded-none" data-testid="kyc-doc-type">
                      <SelectValue placeholder={t('kyc.selectDocumentType')} />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      {documentTypes.map((d) => (<SelectItem key={d.value} value={d.value} className="text-brand-secondary hover:bg-brand-primary/10">{d.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600 font-semibold">{t('kyc.documentNumber')}</Label>
                  <Input value={formData.document_number} onChange={(e) => handleInputChange('document_number', e.target.value)}
                    placeholder="AB1234567" className="h-12 bg-white border-slate-200 focus:border-brand-primary text-brand-secondary rounded-none" data-testid="kyc-doc-number" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-600 font-semibold">{t('kyc.city')}</Label>
                    <Input value={formData.city} onChange={(e) => handleInputChange('city', e.target.value)} placeholder="New York"
                      className="h-12 bg-white border-slate-200 focus:border-brand-primary text-brand-secondary rounded-none" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600 font-semibold">{t('kyc.postalCode')}</Label>
                    <Input value={formData.postal_code} onChange={(e) => handleInputChange('postal_code', e.target.value)} placeholder="10001"
                      className="h-12 bg-white border-slate-200 focus:border-brand-primary text-brand-secondary rounded-none" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600 font-semibold">{t('kyc.country')}</Label>
                  <Select value={formData.country} onValueChange={(v) => handleInputChange('country', v)}>
                    <SelectTrigger className="h-12 bg-white border-slate-200 text-brand-secondary rounded-none">
                      <SelectValue placeholder={t('kyc.selectCountry')} />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      {countries.map((c) => (<SelectItem key={c} value={c} className="text-brand-secondary hover:bg-brand-primary/10">{c}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600 font-semibold">{t('kyc.streetAddress')}</Label>
                  <Input value={formData.address} onChange={(e) => handleInputChange('address', e.target.value)} placeholder="123 Main Street, Apt 4B"
                    className="h-12 bg-white border-slate-200 focus:border-brand-primary text-brand-secondary rounded-none" />
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-600 font-semibold">{t('kyc.accreditationType')}</Label>
                  <Select value={formData.accreditation_type} onValueChange={(v) => handleInputChange('accreditation_type', v)}>
                    <SelectTrigger className="h-12 bg-white border-slate-200 text-brand-secondary rounded-none" data-testid="kyc-accreditation">
                      <SelectValue placeholder={t('kyc.selectAccreditation')} />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      {accreditationTypes.map((a) => (<SelectItem key={a.value} value={a.value} className="text-brand-secondary hover:bg-brand-primary/10">{a.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600 font-semibold">{t('kyc.additionalDetails')}</Label>
                  <Input value={formData.accreditation_details} onChange={(e) => handleInputChange('accreditation_details', e.target.value)}
                    placeholder="e.g., CFA Charterholder since 2020" className="h-12 bg-white border-slate-200 focus:border-brand-primary text-brand-secondary rounded-none" />
                </div>
                <div className="p-4 bg-[#F8FAFC] border border-slate-200">
                  <p className="text-sm text-slate-500">
                    <strong className="text-brand-secondary">{t('kyc.note')}</strong> {t('kyc.demoNote')}
                  </p>
                </div>
              </div>
            )}

            {currentStep === 4 && (
              <div className="space-y-4">
                <h3 className="text-lg font-heading text-brand-secondary">{t('kyc.reviewInfo')}</h3>
                <div className="w-12 h-px bg-brand-primary" />
                <div className="space-y-3">
                  <div className="p-3 bg-[#F8FAFC] border border-slate-100">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{t('kyc.fullName')}</p>
                    <p className="text-brand-secondary font-semibold">{formData.full_name || '-'}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-[#F8FAFC] border border-slate-100">
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{t('kyc.dateOfBirth')}</p>
                      <p className="text-brand-secondary font-semibold">{formData.date_of_birth || '-'}</p>
                    </div>
                    <div className="p-3 bg-[#F8FAFC] border border-slate-100">
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{t('kyc.nationality')}</p>
                      <p className="text-brand-secondary font-semibold">{formData.nationality || '-'}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-[#F8FAFC] border border-slate-100">
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{t('kyc.documentType')}</p>
                      <p className="text-brand-secondary font-semibold capitalize">{formData.document_type?.replace('_', ' ') || '-'}</p>
                    </div>
                    <div className="p-3 bg-[#F8FAFC] border border-slate-100">
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{t('kyc.documentNumber')}</p>
                      <p className="text-brand-secondary font-semibold">{formData.document_number || '-'}</p>
                    </div>
                  </div>
                  <div className="p-3 bg-[#F8FAFC] border border-slate-100">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{t('kyc.accreditation')}</p>
                    <p className="text-brand-secondary font-semibold capitalize">{formData.accreditation_type?.replace('_', ' ') || '-'}</p>
                  </div>
                </div>
                <div className="p-4 bg-brand-primary/10 border border-brand-primary/30">
                  <p className="text-sm text-brand-secondary">{t('kyc.certify')}</p>
                </div>
              </div>
            )}

            <div className="flex justify-between mt-8">
              <Button onClick={prevStep} disabled={currentStep === 1} variant="outline"
                className="h-14 px-6 border-slate-200 text-brand-secondary hover:bg-slate-50 rounded-none disabled:opacity-50">
                <ArrowLeft className="w-4 h-4 mr-2" /> {t('kyc.back')}
              </Button>
              {currentStep < 4 ? (
                <Button onClick={nextStep} className="h-14 px-8 bg-brand-primary hover:bg-brand-primary-hover text-brand-secondary font-semibold rounded-none" data-testid="kyc-next-btn">
                  {t('kyc.continue')} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button onClick={submitKYC} disabled={loading} className="h-14 px-8 bg-brand-primary hover:bg-brand-primary-hover text-brand-secondary font-semibold rounded-none" data-testid="kyc-submit-btn">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-2" /> {t('kyc.submit')}</>}
                </Button>
              )}
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  )
}
